import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import { List, Map } from 'immutable';

import Grid from '../../helpers/AgGridTable';
import { Card } from '../../helpers/Card';
import { GridCard } from '../../helpers/GridCard';
import { IconButton } from '../../helpers/IconButton';
import { ResizableColumns } from '../../helpers/ResizableColumns';
import { ResizableSection } from '../../helpers/ResizableSection';
import { Spinner } from '../../helpers/Spinner';
import config from '../../../config';

import { Menu } from './Menu';
import TextEditor from './TextEditor';

import style from './style.module.css';

/** To fix issue: "Cannot refresh cells with cellRendererFramework". */
const PriComponent = ({ data }) => <span>{data.pri.value}</span>;
const CommentComponent = ({ data }) => <span>{data.bsnClient.value}</span>;
const HarveyCommentComponent = ({ data }) => <span>{data.harveyComment.value}</span>;
const NaDateRender = ({ data }) => <span>{data.naDate.isValid() ? data.naDate.format('MM/DD/YYYY') : ''}</span>;
const LaDateRender = ({ data }) => <span>{data.laDate.isValid() ? data.laDate.format('MM/DD/YYYY') : ''}</span>;

const ApprovedRender = ({ data, colDef }) => {
  const onClick = approved => {
    colDef.onClick({
      index: data.order,
      targetBuyerId: data.linkId,
      approved,
    });
  };

  if (data.loading) {
    return <Spinner className="text-primary" size="small" inline />;
  }

  if (data.approved.value === true) {
    return (
      <IconButton
        icon="check"
        onClick={() => onClick(null)}
        size="sm"
        title="Approved, click to reset"
        variant="success"
        active
      />
    );
  }

  if (data.approved.value === false) {
    return (
      <IconButton
        icon="times"
        onClick={() => onClick(null)}
        size="sm"
        title="Rejected, click to reset"
        variant="danger"
        active
      />
    );
  }

  return (
    <div className="d-flex">
      <IconButton icon="check" onClick={() => onClick(true)} size="sm" title="Approve" variant="success" />
      <IconButton icon="times" onClick={() => onClick(false)} size="sm" title="Reject" variant="danger" />
    </div>
  );
};

/**
 * Side menu component.
 *
 * @param props {Object}.
 * @param approvals {Immutable.List} Approval list.
 * @param associates {Immutable.List} Associate list.
 * @param common {Immutable.Map} Common Object.
 * @param onSort {Function} Handle event of sorting associate table.
 * @param onLoad {Function} Handle event of loading more.
 * @param onSelect {Function} Handle event of selecting a menu item.
 * @param {boolean} props.isNoApproval No approval list selected state.
 * @class
 */
const BodySectionComponent = ({
  approvals,
  associates,
  common,
  onSort,
  onLoad,
  onSelect,
  onOpenLink,
  isNoApproval,
  onUpdateCompany,
  onClick,
}) => {
  const [api, setApi] = useState(null);

  useEffect(() => {
    if (api) {
      api.sizeColumnsToFit();
    }
  }, [api]);

  const onBlur = () => {
    if (api) {
      api.stopEditing();
    }
  };

  const columnDefs = config.tables.getIn(['approval', 'associate']).map(column => {
    switch (column.get('field')) {
      case 'approved':
        return column.merge({
          cellRendererFramework: ApprovedRender,
          onClick,
        });

      case 'revenue':
        return column.merge({
          cellEditorFramework: TextEditor,
          editable: true,
          placeHolder: 'Revenue',
          onBlur,
          isValid: value => !isNaN(value),
        });

      case 'pri':
        return column.merge({
          cellRendererFramework: PriComponent,
          cellEditorFramework: TextEditor,
          editable: true,
          placeHolder: 'pri',
          onBlur,
          isValid: value => !value || value.length <= 10,
        });

      case 'bsnClient':
        return column.merge({
          cellRendererFramework: CommentComponent,
          cellEditorFramework: TextEditor,
          editable: true,
          placeHolder: 'Client Comments',
          onBlur,
          isValid: () => true,
        });

      case 'harveyComment':
        return column.merge({
          cellRendererFramework: HarveyCommentComponent,
          cellEditorFramework: TextEditor,
          editable: true,
          placeHolder: 'Harvey comments',
          onBlur,
          isValid: () => true,
        });

      case 'employees':
        return column.merge({
          cellEditorFramework: TextEditor,
          editable: true,
          placeHolder: 'Employees',
          onBlur,
          isValid: value => !isNaN(value),
        });

      case 'naDate':
        return column.set('cellRendererFramework', NaDateRender);

      case 'laDate':
        return column.set('cellRendererFramework', LaDateRender);

      default:
        return column;
    }
  });

  const onGridReady = params => {
    const value = params.api;

    value.showLoadingOverlay();
    setApi(value);
  };

  const onCellClicked = event => {
    const {
      column: { colId },
      node: { rowIndex },
    } = event;

    if (['revenue', 'pri', 'employees', 'bsnClient', 'harveyComment'].indexOf(colId) > -1) {
      api.startEditingCell({
        rowIndex,
        colKey: colId,
      });
    } else {
      api.stopEditing();
    }
  };

  const onCellValueChanged = params => {
    const {
      oldValue,
      newValue,
      colDef: { field, isValid },
      data: { targetId, linkId, order },
    } = params; // linkId = target_buyer_id
    let normalizedOldValue = oldValue;
    let normalizedNewValue = newValue;
    let buyerId = null;

    if (field === 'pri' || field === 'bsnClient' || field === 'harveyComment') {
      normalizedOldValue = oldValue.value;
      normalizedNewValue = newValue.value;
      buyerId = linkId;
    }

    if (normalizedOldValue !== normalizedNewValue && isValid(normalizedNewValue)) {
      onUpdateCompany(
        { [field === 'pri' ? 'priority' : field]: normalizedNewValue || null },
        {
          companyId: targetId,
          buyerId,
          merge: { name: field, value: normalizedNewValue, index: order },
        },
      );
    }
  };

  const getRowNodeId = data => data.order?.toString();

  return (
    <ResizableSection className={style.host} defaultHeight={480} storageKey="approvalListHeight">
      <ResizableColumns defaultWidth={14} minColumnWidth={160} storageKey="approvalListMenuWidth">
        <Card title="Approval Lists" flush>
          <Menu approvals={approvals} common={common} isNoApproval={isNoApproval} onSelect={onSelect} />
        </Card>
        <GridCard>
          <Grid
            key={common.getIn(['approval', 'id'])}
            columnDefs={columnDefs}
            getRowNodeId={getRowNodeId}
            isFetching={common.get('associatesLoading')}
            onCellClicked={onCellClicked}
            onCellValueChanged={onCellValueChanged}
            onGetNextPageData={onLoad}
            onGridReady={onGridReady}
            onRowDoubleClicked={onOpenLink}
            onSortModelChanged={onSort}
            page={common.getIn(['paging', 'currentPage'])}
            rowData={associates}
            totalPages={common.getIn(['paging', 'totalPages'])}
            serverSideSorting
            sortable
          />
        </GridCard>
      </ResizableColumns>
    </ResizableSection>
  );
};

BodySectionComponent.propTypes = {
  approvals: PropTypes.instanceOf(List).isRequired,
  associates: PropTypes.instanceOf(List).isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  isNoApproval: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  onOpenLink: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onUpdateCompany: PropTypes.func.isRequired,
};

/** Body section component. */
export const BodySection = memo(BodySectionComponent);
